/* eslint-disable react-hooks/exhaustive-deps */
import { Helmet } from 'react-helmet-async'
import { useState, useCallback, useEffect, useRef, useMemo, memo, startTransition } from 'react'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress'
import Autocomplete from '@mui/material/Autocomplete'
import InputAdornment from '@mui/material/InputAdornment'
import LinearProgress from '@mui/material/LinearProgress'
import Divider from '@mui/material/Divider'
import Skeleton from '@mui/material/Skeleton'
import { alpha, useTheme, type Theme } from '@mui/material/styles'
import { GlobalStyles } from '@mui/material'
import Notiflix from 'notiflix'

import { format } from 'date-fns' // eslint-disable-line
import ptBR from 'date-fns/locale/pt-BR' // eslint-disable-line

import Iconify from 'src/components/iconify'
import axiosInstance from 'src/utils/axios'
import useEvents from 'src/service/events.service'
import useDebounce from 'src/hooks/use-debounce'

// --------------------------------------------------------------------------

type StockProduct = {
  _id: string
  name: string
  photo?: string
  price: number
  stock: number
  minStock: number | null
  maxStock: number | null
  effectiveMinStock: number
  effectiveMaxStock: number
  category: string
  stockLevel: 'critical' | 'warning' | 'ok'
  active: boolean
}

type RowValues = {
  stock: string
  minStock: string
  maxStock: string
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error'

type StockLevelFilter = 'all' | 'critical' | 'warning' | 'ok'

const STOCK_LEVEL_KEY = {
  critical: { label: 'Crítico', color: 'error' as const },
  warning: { label: 'Atenção', color: 'warning' as const },
  ok: { label: 'OK', color: 'success' as const },
}

function toRowValues(p: StockProduct): RowValues {
  return {
    stock: String(p.stock),
    minStock: p.minStock != null ? String(p.minStock) : '',
    maxStock: p.maxStock != null ? String(p.maxStock) : '',
  }
}

function stockInputStyles(theme: Theme) {
  return {
    '.stock-input': {
      width: 56,
      height: 26,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 6,
      textAlign: 'center' as const,
      fontSize: '0.8rem',
      fontFamily: theme.typography.fontFamily,
      background: theme.palette.background.paper,
      color: theme.palette.text.primary,
      outline: 'none',
      transition: 'border-color 0.15s',
      MozAppearance: 'textfield' as any,
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
      '&:focus': {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`,
      },
    },
    '.stock-input-wide': {
      width: 64,
    },
    '.stock-badge': {
      display: 'inline-block',
      fontSize: 10,
      fontWeight: 700,
      fontFamily: theme.typography.fontFamily,
      lineHeight: '20px',
      height: 20,
      padding: '0 8px',
      borderRadius: 10,
    },
    '.stock-badge-critical': {
      background: alpha(theme.palette.error.main, 0.12),
      color: theme.palette.error.main,
    },
    '.stock-badge-warning': {
      background: alpha(theme.palette.warning.main, 0.12),
      color: theme.palette.warning.dark,
    },
    '.stock-badge-ok': {
      background: alpha(theme.palette.success.main, 0.12),
      color: theme.palette.success.dark,
    },
    '.stock-bar-track': {
      width: 56,
      height: 5,
      borderRadius: 3,
      background: theme.palette.action.hover,
      overflow: 'hidden',
    },
    '.stock-bar-fill': {
      height: '100%',
      borderRadius: 3,
      transition: 'width 0.2s',
    },
  }
}

// --------------------------------------------------------------------------

type StockRowProps = {
  product: StockProduct
  rowIdx: number
  vals: RowValues
  saveState: SaveState
  bgColor: string
  barColor: string
  onChange: (id: string, field: keyof RowValues, value: string) => void
  onBlur: (product: StockProduct) => void
  onNavigate: (e: React.KeyboardEvent, rowIdx: number, col: string) => void
}

const StockRow = memo(({
  product,
  rowIdx,
  vals,
  saveState,
  bgColor,
  barColor,
  onChange,
  onBlur,
  onNavigate,
}: StockRowProps) => {
  const levelCfg = STOCK_LEVEL_KEY[product.stockLevel]
  const pct =
    product.effectiveMaxStock > 0
      ? Math.min((product.stock / product.effectiveMaxStock) * 100, 100)
      : 0

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select()
  }, [])

  return (
    <tr style={{ backgroundColor: bgColor, transition: 'background 0.15s' }}>
      <td style={{ padding: '3px 8px 3px 16px' }}>
        <div style={{ fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 }}>
          {product.name}
        </div>
        <div style={{ fontSize: '0.7rem', color: '#919EAB' }}>
          {product.category}
        </div>
      </td>

      <td style={{ padding: '3px 8px', textAlign: 'center' }}>
        <span className={`stock-badge stock-badge-${product.stockLevel}`}>
          {levelCfg.label}
        </span>
      </td>

      <td style={{ padding: '3px 8px', textAlign: 'center' }}>
        <input
          className='stock-input stock-input-wide'
          type='number'
          min={0}
          value={vals.stock}
          onChange={(e) => onChange(product._id, 'stock', e.target.value)}
          onBlur={() => onBlur(product)}
          onFocus={handleFocus}
          onKeyDown={(e) => onNavigate(e, rowIdx, 'stock')}
          data-row={rowIdx}
          data-col='stock'
        />
      </td>

      <td style={{ padding: '3px 8px', textAlign: 'center' }}>
        <input
          className='stock-input'
          type='number'
          min={0}
          placeholder='10'
          value={vals.minStock}
          onChange={(e) => onChange(product._id, 'minStock', e.target.value)}
          onBlur={() => onBlur(product)}
          onFocus={handleFocus}
          onKeyDown={(e) => onNavigate(e, rowIdx, 'minStock')}
          data-row={rowIdx}
          data-col='minStock'
        />
      </td>

      <td style={{ padding: '3px 8px', textAlign: 'center' }}>
        <input
          className='stock-input'
          type='number'
          min={0}
          placeholder='25'
          value={vals.maxStock}
          onChange={(e) => onChange(product._id, 'maxStock', e.target.value)}
          onBlur={() => onBlur(product)}
          onFocus={handleFocus}
          onKeyDown={(e) => onNavigate(e, rowIdx, 'maxStock')}
          data-row={rowIdx}
          data-col='maxStock'
        />
      </td>

      <td style={{ padding: '3px 8px', textAlign: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div className='stock-bar-track'>
            <div
              className='stock-bar-fill'
              style={{ width: `${pct}%`, backgroundColor: barColor }}
            />
          </div>
          <span style={{ fontSize: '0.65rem', color: '#919EAB', lineHeight: 1.4 }}>
            {Math.round(pct)}%
          </span>
        </div>
      </td>

      <td style={{ padding: '3px 6px 3px 4px', textAlign: 'center', width: 28 }}>
        {saveState === 'saving' && <CircularProgress size={14} thickness={5} />}
        {saveState === 'saved' && (
          <Box component='span' sx={{ display: 'inline-flex', color: 'success.main' }}>
            <Iconify icon='carbon:checkmark-filled' width={14} />
          </Box>
        )}
        {saveState === 'error' && (
          <Tooltip title='Erro ao salvar'>
            <Box component='span' sx={{ display: 'inline-flex', color: 'error.main' }}>
              <Iconify icon='carbon:warning-filled' width={14} />
            </Box>
          </Tooltip>
        )}
      </td>
    </tr>
  )
})

// --------------------------------------------------------------------------

export default function StockListView() {
  const theme = useTheme()
  const [eventQuery, setEventQuery] = useState('')
  const [selectedEvent, setSelectedEvent] = useState<any>(null)
  const searchDebounce = useDebounce(eventQuery, 400)

  const [products, setProducts] = useState<StockProduct[]>([])
  const [loadingProducts, setLoadingProducts] = useState(false)

  const [rowValues, setRowValues] = useState<Record<string, RowValues>>({})
  const [saveStates, setSaveStates] = useState<Record<string, SaveState>>({})
  const originalValues = useRef<Record<string, RowValues>>({})
  const tableRef = useRef<HTMLTableElement>(null)

  const [statusFilter, setStatusFilter] = useState<StockLevelFilter>('all')
  const [searchProduct, setSearchProduct] = useState('')
  const [deferredFilter, setDeferredFilter] = useState<StockLevelFilter>('all')
  const [deferredSearch, setDeferredSearch] = useState('')
  const [saveCount, setSaveCount] = useState(0)

  const debouncedSearch = useDebounce(searchProduct, 200)

  useEffect(() => {
    startTransition(() => setDeferredSearch(debouncedSearch))
  }, [debouncedSearch])

  useEffect(() => {
    startTransition(() => setDeferredFilter(statusFilter))
  }, [statusFilter])

  const navigateCell = useCallback((e: React.KeyboardEvent, rowIdx: number, col: string) => {
    if (!tableRef.current) return
    const cols = ['stock', 'minStock', 'maxStock']
    const colIdx = cols.indexOf(col)

    let nextRow = rowIdx
    const nextCol = colIdx

    if (e.key === 'Enter' || e.key === 'ArrowDown') {
      e.preventDefault()
      nextRow = rowIdx + 1
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      nextRow = rowIdx - 1
    } else {
      return
    }

    const target = tableRef.current.querySelector<HTMLInputElement>(
      `input[data-row="${nextRow}"][data-col="${cols[nextCol]}"]`,
    )
    if (target) {
      target.focus()
      target.select()
    }
  }, [])

  const activeSearch = selectedEvent ? '' : searchDebounce

  const { data: dataEvent, isLoading: loadingEvents } = useEvents(
    { page: 1, perPage: 100, sort: undefined },
    { search: activeSearch },
  )

  const eventOptions = useMemo(
    () =>
      dataEvent?.data?.map((ev: any) => ({
        label: ev.name,
        value: ev._id,
        date: ev.date,
        local: ev.local || '',
      })) || [],
    [dataEvent],
  )

  const fetchStock = useCallback(async (eventId: string, isRefresh = false) => {
    try {
      if (!isRefresh) setLoadingProducts(true)
      const { data } = await axiosInstance.get('/product/stock', {
        params: { event: eventId },
      })
      setProducts(data)
      const initial: Record<string, RowValues> = {}
      data.forEach((p: StockProduct) => {
        initial[p._id] = toRowValues(p)
      })
      setRowValues(initial)
      originalValues.current = initial
      setSaveStates({})
      if (!isRefresh) {
        setSaveCount(0)
        setStatusFilter('all')
        setSearchProduct('')
      }
    } catch {
      Notiflix.Notify.failure('Erro ao carregar estoque.')
    } finally {
      setLoadingProducts(false)
    }
  }, [])

  useEffect(() => {
    if (selectedEvent?.value) fetchStock(selectedEvent.value)
  }, [selectedEvent])

  const handleChange = useCallback((id: string, field: keyof RowValues, value: string) => {
    setRowValues((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }))
    setSaveStates((prev) => {
      if (prev[id] === 'saved') return { ...prev, [id]: 'idle' }
      return prev
    })
  }, [])

  const handleBlur = useCallback(
    async (product: StockProduct) => {
      const current = rowValues[product._id]
      const original = originalValues.current[product._id]
      if (!current || !original) return

      if (
        current.stock === original.stock &&
        current.minStock === original.minStock &&
        current.maxStock === original.maxStock
      )
        return

      const stockVal = Number(current.stock)
      const minVal = current.minStock !== '' ? Number(current.minStock) : null
      const maxVal = current.maxStock !== '' ? Number(current.maxStock) : null

      if (minVal !== null && maxVal !== null && minVal >= maxVal) {
        Notiflix.Notify.warning('Estoque mínimo deve ser menor que o máximo.')
        setRowValues((prev) => ({ ...prev, [product._id]: original }))
        return
      }

      setSaveStates((prev) => ({ ...prev, [product._id]: 'saving' }))
      try {
        await axiosInstance.put(`/product/${product._id}`, {
          stock: stockVal,
          minStock: minVal,
          maxStock: maxVal,
        })
        originalValues.current[product._id] = current
        setSaveStates((prev) => ({ ...prev, [product._id]: 'saved' }))
        setSaveCount((c) => c + 1)

        const effectiveMin = minVal ?? 10
        const effectiveMax = maxVal ?? 25
        let newLevel: StockProduct['stockLevel'] = 'ok'
        if (stockVal <= effectiveMin) newLevel = 'critical'
        else if (stockVal <= effectiveMax) newLevel = 'warning'

        setProducts((prev) =>
          prev.map((p) =>
            p._id === product._id
              ? { ...p, stock: stockVal, minStock: minVal, maxStock: maxVal, effectiveMinStock: effectiveMin, effectiveMaxStock: effectiveMax, stockLevel: newLevel }
              : p,
          ),
        )
      } catch {
        Notiflix.Notify.failure('Erro ao salvar. Tente novamente.')
        setSaveStates((prev) => ({ ...prev, [product._id]: 'error' }))
        setRowValues((prev) => ({ ...prev, [product._id]: original }))
      }
    },
    [rowValues],
  )

  const filteredProducts = useMemo(() => {
    let list = products
    if (deferredFilter !== 'all') {
      list = list.filter((p) => p.stockLevel === deferredFilter)
    }
    if (deferredSearch.trim()) {
      const q = deferredSearch.toLowerCase()
      list = list.filter((p) => p.name.toLowerCase().includes(q) || p.category.toLowerCase().includes(q))
    }
    return list
  }, [products, deferredFilter, deferredSearch])

  const summary = useMemo(
    () => ({
      total: products.length,
      critical: products.filter((p) => p.stockLevel === 'critical').length,
      warning: products.filter((p) => p.stockLevel === 'warning').length,
      ok: products.filter((p) => p.stockLevel === 'ok').length,
    }),
    [products],
  )

  const summaryCards = useMemo(
    () => [
      { label: 'Todos', value: summary.total, color: theme.palette.primary.main, bg: alpha(theme.palette.primary.main, 0.08), icon: 'carbon:package', filterKey: 'all' as StockLevelFilter },
      { label: 'Crítico', value: summary.critical, color: theme.palette.error.main, bg: alpha(theme.palette.error.main, 0.08), icon: 'solar:danger-bold', filterKey: 'critical' as StockLevelFilter },
      { label: 'Atenção', value: summary.warning, color: theme.palette.warning.main, bg: alpha(theme.palette.warning.main, 0.08), icon: 'solar:shield-warning-bold', filterKey: 'warning' as StockLevelFilter },
      { label: 'OK', value: summary.ok, color: theme.palette.success.main, bg: alpha(theme.palette.success.main, 0.08), icon: 'solar:shield-check-bold', filterKey: 'ok' as StockLevelFilter },
    ],
    [summary, theme],
  )

  const rowColorMap = useMemo(() => {
    const map: Record<string, { bg: string; bar: string }> = {}
    products.forEach((p) => {
      const cfg = STOCK_LEVEL_KEY[p.stockLevel]
      map[p._id] = {
        bg: alpha(theme.palette[cfg.color].main, 0.04),
        bar: theme.palette[cfg.color].main,
      }
    })
    return map
  }, [products, theme])

  const globalStyles = useMemo(() => stockInputStyles(theme), [theme])

  return (
    <>
      <Helmet>
        <title>Gestão de Estoque</title>
      </Helmet>
      <GlobalStyles styles={globalStyles} />

      <Container maxWidth='xl'>
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 2,
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            mb: 2,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ position: 'absolute', top: -40, right: -40, width: 120, height: 120, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
          <Stack direction='row' alignItems='center' spacing={1.5} sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Iconify icon='carbon:inventory-management' width={20} />
            </Box>
            <Box>
              <Typography fontWeight='bold' sx={{ fontSize: { xs: '1.1rem', sm: '1.4rem' }, lineHeight: 1.2 }}>
                Gestão de Estoque
              </Typography>
              <Typography sx={{ opacity: 0.7, fontSize: '0.75rem' }}>
                Edite diretamente na tabela — salvo ao sair do campo
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Seletor de evento */}
        <Card sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
          <Stack direction='row' spacing={1.5} alignItems='center'>
            <Autocomplete
              fullWidth
              options={eventOptions}
              loading={loadingEvents}
              value={selectedEvent}
              onChange={(_e, val) => setSelectedEvent(val)}
              inputValue={eventQuery}
              onInputChange={(_e, val) => setEventQuery(val)}
              getOptionLabel={(opt) => opt.label}
              isOptionEqualToValue={(opt, val) => opt.value === val.value}
              renderOption={(props, opt) => {
                let dateStr = ''
                try { dateStr = format(new Date(opt.date), "dd MMM yyyy", { locale: ptBR }) } catch { dateStr = '' }
                return (
                  <Box component='li' {...props} key={opt.value}>
                    <Stack direction='row' alignItems='center' spacing={1.5} sx={{ py: 0.3 }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Iconify icon='carbon:calendar' width={16} sx={{ color: 'primary.main' }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant='body2' fontWeight={600} noWrap>{opt.label}</Typography>
                        <Stack direction='row' spacing={1} alignItems='center'>
                          {dateStr && (
                            <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.7rem' }}>
                              {dateStr}
                            </Typography>
                          )}
                          {opt.local && (
                            <Typography variant='caption' color='text.disabled' noWrap sx={{ fontSize: '0.7rem' }}>
                              {opt.local}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                )
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder='Buscar evento por nome...'
                  size='small'
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Iconify icon='carbon:search' width={18} sx={{ color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            {selectedEvent && (
              <Tooltip title='Recarregar estoque'>
                <IconButton
                  size='small'
                  onClick={() => fetchStock(selectedEvent.value, true)}
                  disabled={loadingProducts}
                  sx={{ border: '1px solid', borderColor: 'divider' }}
                >
                  <Iconify icon='carbon:renew' width={18} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Card>

        {/* Resumo — clique filtra */}
        {selectedEvent && !loadingProducts && products.length > 0 && (
          <Stack direction='row' spacing={1.5} mb={2}>
            {summaryCards.map((item) => {
              const isActive = statusFilter === item.filterKey
              return (
                <Card
                  key={item.filterKey}
                  onClick={() => setStatusFilter(isActive ? 'all' : item.filterKey)}
                  sx={{
                    flex: 1,
                    py: 1,
                    px: 1.5,
                    borderRadius: 1.5,
                    border: '2px solid',
                    borderColor: isActive ? item.color : 'divider',
                    bgcolor: isActive ? alpha(item.color, 0.06) : 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: item.color, bgcolor: alpha(item.color, 0.04) },
                  }}
                >
                  <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Iconify icon={item.icon} width={18} sx={{ color: item.color }} />
                  </Box>
                  <Box>
                    <Typography variant='h6' fontWeight={700} lineHeight={1}>{item.value}</Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: isActive ? item.color : 'text.secondary', fontWeight: isActive ? 600 : 400 }}>
                      {item.label}
                    </Typography>
                  </Box>
                </Card>
              )
            })}
          </Stack>
        )}

        {/* Estado vazio: sem evento */}
        {!selectedEvent && (
          <Card sx={{ p: 6, borderRadius: 2, textAlign: 'center', border: '1px dashed', borderColor: 'divider' }}>
            <Iconify icon='carbon:inventory-management' width={56} sx={{ color: 'text.disabled', mb: 2 }} />
            <Typography variant='h6' color='text.secondary' gutterBottom>Nenhum evento selecionado</Typography>
            <Typography variant='body2' color='text.disabled'>Selecione um evento acima para visualizar os produtos monitorados.</Typography>
          </Card>
        )}

        {/* Loading */}
        {selectedEvent && loadingProducts && (
          <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <LinearProgress />
            <Box sx={{ p: 2 }}>
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} height={52} sx={{ mb: 0.5, borderRadius: 1 }} />
              ))}
            </Box>
          </Card>
        )}

        {/* Estado vazio: sem produtos com estoque */}
        {selectedEvent && !loadingProducts && products.length === 0 && (
          <Card sx={{ p: 6, borderRadius: 2, textAlign: 'center', border: '1px dashed', borderColor: 'divider' }}>
            <Iconify icon='carbon:checkmark-outline' width={56} sx={{ color: 'success.main', mb: 2 }} />
            <Typography variant='h6' color='text.secondary' gutterBottom>Nenhum produto com controle de estoque</Typography>
            <Typography variant='body2' color='text.disabled'>Ative o controle de estoque nas categorias do cardápio deste evento.</Typography>
          </Card>
        )}

        {/* Tabela */}
        {selectedEvent && !loadingProducts && products.length > 0 && (
          <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ px: 2, py: 1, bgcolor: 'background.neutral' }}>
              <TextField
                size='small'
                placeholder='Buscar produto...'
                value={searchProduct}
                onChange={(e) => setSearchProduct(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Iconify icon='carbon:search' width={16} sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                  ...(searchProduct && {
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton size='small' onClick={() => setSearchProduct('')} edge='end'>
                          <Iconify icon='carbon:close' width={14} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }),
                }}
                sx={{ width: 220, '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' } }}
              />
              <Stack direction='row' alignItems='center' spacing={1.5}>
                {statusFilter !== 'all' && (
                  <Chip
                    label={`Filtro: ${STOCK_LEVEL_KEY[statusFilter].label}`}
                    size='small'
                    color={STOCK_LEVEL_KEY[statusFilter].color}
                    variant='soft'
                    onDelete={() => setStatusFilter('all')}
                    sx={{ fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                  />
                )}
                <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                  {filteredProducts.length} de {products.length} produtos
                </Typography>
                {saveCount > 0 && (
                  <Chip
                    icon={<Iconify icon='carbon:checkmark-filled' width={14} />}
                    label={`${saveCount} salvo${saveCount > 1 ? 's' : ''}`}
                    size='small'
                    color='success'
                    variant='soft'
                    sx={{ fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                  />
                )}
              </Stack>
            </Stack>
            <Divider />
            <TableContainer>
              <table
                ref={tableRef}
                style={{ width: '100%', borderCollapse: 'collapse', fontFamily: theme.typography.fontFamily as string }}
              >
                <thead>
                  <tr style={{ backgroundColor: theme.palette.background.neutral }}>
                    <th style={{ padding: '6px 8px 6px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.8rem' }}>Produto</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, fontSize: '0.8rem' }}>Status</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, fontSize: '0.8rem' }}>Estoque</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, fontSize: '0.8rem' }}>Mín.</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, fontSize: '0.8rem' }}>Máx.</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, fontSize: '0.8rem' }}>Nível</th>
                    <th style={{ width: 28, padding: '6px 6px 6px 4px' }} />
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product, rowIdx) => {
                    const vals = rowValues[product._id]
                    if (!vals) return null
                    const colors = rowColorMap[product._id]
                    return (
                      <StockRow
                        key={product._id}
                        product={product}
                        rowIdx={rowIdx}
                        vals={vals}
                        saveState={saveStates[product._id] ?? 'idle'}
                        bgColor={colors?.bg ?? 'transparent'}
                        barColor={colors?.bar ?? theme.palette.primary.main}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onNavigate={navigateCell}
                      />
                    )
                  })}
                </tbody>
              </table>
            </TableContainer>

            <Divider />
            <Box sx={{ py: 1, px: 2, bgcolor: 'background.neutral' }}>
              <Typography variant='caption' color='text.disabled' sx={{ fontSize: '0.7rem' }}>
                Salvo automaticamente ao sair do campo. Placeholder (10 / 25) = valor padrão.
              </Typography>
            </Box>
          </Card>
        )}
      </Container>
    </>
  )
}
